"use client";

import { ReactNode, useId, useState } from "react";
import { motion } from "framer-motion";
import { PlusIcon } from "@phosphor-icons/react/ssr";
import { cn } from "~/lib/utils";
import Heading from "~/componentsDS/Typography/Heading";
import Paragraph from "./Typography/Paragraph";
import TextWithRegisteredMark from "~/componentsDS/TextWithRegisteredMark";

export type FAQ = {
    icon?: ReactNode;
    question: string;
    badge?: ReactNode;
    answer: ReactNode;
    dontRenderInsideParagraph?: boolean;
}

type AccordionVariant = "default" | "condensed" | "compact" | "highlight" | "condensed_v2";

type Props = {
    answerClassName?: string;
    className?: string;
    items: FAQ[];
    defaultOpen?: number | undefined;
    onOpen?: (item: FAQ) => void;
    variant?: AccordionVariant;
    onClick?: (questionText: string) => void;
    renderQuestion?: (question: string, item?: FAQ) => ReactNode;
}

const VARIANT_STYLES = {
    default: {
        questionSize: "xs",
        questionSizeMob: "xs",
        questionWeight: "400",
        answerSize: "lg",
        answerSizeMob: "lg",
        paddingY: "py-6",
        iconSize: 32,
    },
    condensed: {
        questionSize: "2xs",
        questionSizeMob: "2xs",
        questionWeight: "400",
        answerSize: "md",
        answerSizeMob: "md",
        paddingY: "py-4",
        iconSize: 24,
    },
    condensed_v2: {
        questionSize: "xs",
        questionSizeMob: "2xs",
        questionWeight: "400",
        answerSize: "md",
        answerSizeMob: "md",
        paddingY: "py-4",
        iconSize: 24,
    },
    compact: {
        questionSize: "3xs",
        questionSizeMob: "3xs",
        questionWeight: "500",
        answerSize: "sm",
        answerSizeMob: "sm",
        paddingY: "py-[14px]",
        iconSize: 24,
    },
    highlight: {
        questionSize: "xs",
        questionSizeMob: "2xs",
        questionWeight: "400",
        answerSize: "md",
        answerSizeMob: "sm",
        paddingY: "py-4 md:py-[26px]",
        iconSize: 32,
    },
} as const;

export const Accordion = ({
    answerClassName,
    className,
    items = [],
    defaultOpen = -1,
    onOpen,
    variant = "default",
    onClick,
    renderQuestion
}: Props) => {
    const [openItem, setOpenItem] = useState(defaultOpen);
    const accordionId = useId();

    const styles = VARIANT_STYLES[variant];

    const toggleItem = (index: number) => {
        const item = items[index];
        if (!item) return;
        const isClosing = index === openItem;

        if (isClosing) {
            setOpenItem(-1);
            return;
        }

        setOpenItem(index);
        onOpen?.(item);

        if (!onClick) return;

        if (typeof item.question === "string") {
            onClick(item.question);
        }
    };

    const renderQuestionContent = (item: FAQ) => {
        const { question } = item;

        if (typeof question === "string" && renderQuestion) {
            return renderQuestion(question, item);
        }

        return question;
    };

    return (
        <div className={cn(variant === "highlight" ? "flex flex-col gap-4" : "border-gray200 border-t", className)}>
            {items.map((item, index) => {
                const buttonId = `${accordionId}-button-${index}`;
                const panelId = `${accordionId}-panel-${index}`;
                const isOpen = openItem === index;

                return (
                    <div key={index} className={cn(
                        variant === "highlight"
                            ? "border border-primary300 text-primary300 rounded-[20px] px-6 transition-color duration-300 ease-in-out"
                            : "border-gray200 border-b",
                        variant === "highlight" && openItem === index && "bg-secondary300 text-trueBlack border-secondary300"
                    )}>
                        <button
                            id={buttonId}
                            type="button"
                            aria-controls={panelId}
                            aria-expanded={isOpen}
                            onClick={() => toggleItem(index)}
                            className={cn(
                                "flex items-center justify-between w-full text-left",
                                styles.paddingY
                            )}
                        >
                            <Heading
                                as="h3"
                                className="flex gap-2 items-center"
                                size={styles.questionSizeMob}
                                sizes={{md: styles.questionSize}}
                                weight={styles.questionWeight}
                            >
                                {item.icon && (
                                    <span className="inline-block shrink-0">{item.icon}</span>
                                )}
                                <span className="inline-block pt-[2px]">
                                    {renderQuestion ? renderQuestionContent(item) : (
                                        <>
                                            <TextWithRegisteredMark text={item.question} />
                                            {item.badge}
                                        </>
                                    )}
                                </span>
                            </Heading>

                            <PlusIcon
                                className={cn("duration-200 ml-[10px] shrink-0", {
                                    "rotate-45": openItem === index,
                                })}
                                size={styles.iconSize}
                            />
                        </button>

                        <motion.div
                            id={panelId}
                            role="region"
                            aria-labelledby={buttonId}
                            animate={{
                                height: isOpen ? "auto" : 0,
                                opacity: isOpen ? 1 : 0,
                            }}
                            initial={false}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="overflow-hidden pr-6"
                        >
                            <div
                                className={cn(
                                    "pb-6",
                                    item.icon && "pl-8"
                                )}
                            >
                                {item.dontRenderInsideParagraph ? (
                                    item.answer
                                ) : (
                                    <Paragraph
                                        size={styles.answerSizeMob}
                                        sizes={{md: styles.answerSize}}
                                        className={answerClassName}
                                    >
                                        {typeof item.answer === "string" ? <TextWithRegisteredMark text={item.answer} /> : item.answer}
                                    </Paragraph>
                                )}
                            </div>
                        </motion.div>
                    </div>
                );
            })}
        </div>
    );
};

export default Accordion;
